import {
  listAdminOrdersPage,
  type AdminOrdersPage,
  type AdminOrdersPageParams,
} from '@shopgnd/api';
import type { Order } from '@shopgnd/shared';
import { listAllOrders } from '@/api/orders';
import { isAdminOrdersApiConfigured, webApiConfig } from '@/lib/api-config';

let appSyncFallbackCache: Order[] | null = null;

/** Slow path when `custom.adminOrdersUrl` is missing from amplify_outputs.json. */
async function fetchAdminOrdersPageViaAppSync(
  params: AdminOrdersPageParams,
): Promise<AdminOrdersPage> {
  if (!appSyncFallbackCache) {
    console.warn(
      '[ShopGND] adminOrdersUrl is not in amplify_outputs.json — listing orders via AppSync. ' +
        'Run `npm run sandbox`, then copy amplify_outputs.json to apps/web/public/.',
    );
    appSyncFallbackCache = await listAllOrders();
  }

  const limit = Math.min(Math.max(params.limit ?? 50, 1), 100);
  const offset = params.cursor ? Number.parseInt(params.cursor, 10) : 0;
  if (!Number.isFinite(offset) || offset < 0) {
    return { orders: [], hasMore: false };
  }

  const orders = appSyncFallbackCache.slice(offset, offset + limit);
  const nextOffset = offset + limit;
  const hasMore = nextOffset < appSyncFallbackCache.length;

  return {
    orders,
    nextCursor: hasMore ? String(nextOffset) : undefined,
    hasMore,
  };
}

export { isAdminOrdersApiConfigured };

export async function fetchAdminOrdersPage(
  params: AdminOrdersPageParams = {},
): Promise<AdminOrdersPage> {
  try {
    const { isAdminApiConfigured, adminListOrders } = await import('@/api/adminPlatform');
    if (await isAdminApiConfigured()) {
      const page = await adminListOrders({
        limit: params.limit,
        status: params.status,
        cursor: params.cursor,
      });
      return {
        orders: (page.orders ?? []) as Order[],
        nextCursor: page.nextCursor ?? undefined,
        hasMore: Boolean(page.nextCursor),
      };
    }
  } catch (e) {
    console.warn('[ShopGND] admin-api orders failed, falling back', e);
  }

  if (await isAdminOrdersApiConfigured()) {
    return listAdminOrdersPage(webApiConfig, params);
  }
  return fetchAdminOrdersPageViaAppSync(params);
}

/** First page only — for dashboards that need a sample, not the full table. */
export async function fetchAdminOrdersSample(limit = 50) {
  const page = await fetchAdminOrdersPage({ limit });
  return page.orders;
}
